import axios from 'axios';
import { SET_AUTH, SET_ROLE } from '../Redux/Actions/AuthActions';
import { isAbortError } from './isAbortError.js';
import { syncSessionEmailFromToken } from './authSession.js';

function delayWithSignal(ms, signal) {
    if (!signal) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    return new Promise((resolve, reject) => {
        const onAbort = () => {
            clearTimeout(t);
            signal.removeEventListener('abort', onAbort);
            reject(new DOMException('Aborted', 'AbortError'));
        };
        const t = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        if (signal.aborted) {
            clearTimeout(t);
            onAbort();
            return;
        }
        signal.addEventListener('abort', onAbort);
    });
}

async function callRefresh(options = {}) {
    const { signal } = options;
    const userToken = localStorage.getItem('userToken');
    if (!userToken) {
        return null;
    }

    try {
        const response = await axios.post(
            `${import.meta.env.VITE_BASE_URL}/account/refresh-token`,
            null,
            { headers: { Authorization: `Bearer ${userToken}` }, signal }
        );
        return response.data ?? null;
    } catch (error) {
        if (isAbortError(error)) {
            throw error;
        }
        console.warn('Failed to refresh user token:', error.response?.data ?? error.message);
        return null;
    }
}

function applyRefreshResult(data, dispatch) {
    if (!data) {
        return;
    }

    const { token, roleId } = data;

    if (token) {
        localStorage.setItem('userToken', token);
        syncSessionEmailFromToken(token);
    }

    if (roleId !== undefined && roleId !== null) {
        localStorage.setItem('roleId', String(roleId));
    }

    if (dispatch) {
        dispatch({ type: SET_AUTH, payload: true });
        if (roleId !== undefined && roleId !== null) {
            dispatch({ type: SET_ROLE, payload: roleId });
        }
    }
}

/**
 * Calls /account/refresh-token to re-issue a JWT containing the latest user roles.
 * Updates `localStorage.userToken` / `roleId` and dispatches Redux auth actions.
 *
 * @param {(action: any) => void} [dispatch]
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ token: string, roleId?: number } | null>}
 */
export async function refreshUserToken(dispatch, options = {}) {
    const data = await callRefresh(options);
    applyRefreshResult(data, dispatch);
    return data;
}

/**
 * Same as refreshUserToken, but retries while roleId stays the same as before.
 * Useful right after a purchase, where the role assignment is processed
 * asynchronously through Service Bus and may not be visible immediately.
 *
 * Safe to fire-and-forget: keeps running in the background even if the
 * caller's component unmounts (Redux store and localStorage are global).
 *
 * @param {(action: any) => void} [dispatch]
 * @param {{ maxAttempts?: number, delayMs?: number, signal?: AbortSignal }} [options]
 */
export async function refreshUserTokenAfterPurchase(dispatch, options = {}) {
    const { maxAttempts = 4, delayMs = 1000, signal } = options;
    const previousRoleId = localStorage.getItem('roleId') ?? '';

    let lastData = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (signal?.aborted) {
            return lastData;
        }

        let data;
        try {
            data = await callRefresh({ signal });
        } catch (error) {
            if (isAbortError(error)) {
                return lastData;
            }
            data = null;
        }
        lastData = data ?? lastData;

        if (data) {
            applyRefreshResult(data, dispatch);

            const newRoleId = data.roleId !== undefined && data.roleId !== null
                ? String(data.roleId)
                : '';

            if (newRoleId && newRoleId !== previousRoleId) {
                return data;
            }
        }

        if (attempt < maxAttempts - 1) {
            try {
                await delayWithSignal(delayMs, signal);
            } catch (e) {
                if (e?.name === 'AbortError') {
                    return lastData;
                }
                throw e;
            }
        }
    }

    return lastData;
}
